<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from 'vue';
import i18n from '@/localisation/i18n';
import BrokerCompanyDialog from '@/modules/broker-admin/components/BrokerCompanyDialog.vue';
import { errorString } from '@/utils/helpers/rest-response';
import { roundingRuleToShortString } from '@/modules/sec-lending/helpers/contract-details';
import { DataTableHeader } from 'vuetify';
import { store as vuex } from '@/store/store';
import BrokerIpAllowListManager from '@/modules/broker-admin/components/BrokerIpAllowListManager.vue';
import { Company, CompanyListResponse, CompanyRequest } from '@/modules/common/models/admin';
import axios from 'axios';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import logger from '@/modules/common/services/logger.service';
import { useSocketStore } from '@/store/socket-store';

const tableColumns: DataTableHeader[] = [
  { text: '', value: 'icon', sortable: false, width: 16, class: 'row-icon', cellClass: 'row-icon' },
  {
    text: i18n.tc('companyDisplayId'),
    value: 'displayId',
    sortable: true,
    class: 'text-no-wrap text-truncate',
  },
  { text: i18n.tc('traderCompany'), value: 'name', class: 'text-no-wrap text-truncate' },
  {
    text: i18n.tc('companyLoanet.table'),
    value: 'loanetIntegration',
    class: 'text-no-wrap text-truncate',
  },
  {
    text: i18n.tc('deliverOrderSender'),
    value: 'deliverOrderSender',
    class: 'text-no-wrap text-truncate',
  },
  {
    text: i18n.tc('companyCategory'),
    value: 'profileLabel',
    class: 'text-no-wrap text-truncate',
  },
  {
    text: i18n.tc('tradingPermissions.title'),
    value: 'tradingPermissions',
    class: 'text-no-wrap text-truncate',
  },
  {
    text: 'Collateral Rate',
    value: 'preferredIndependentAmountRate',
    class: 'text-no-wrap text-truncate',
  },
  {
    text: 'RR',
    value: 'preferredRoundingRule',
    class: 'text-no-wrap text-truncate',
  },
  { text: '', value: 'actions', sortable: false },
];

// display text for deliver order sender values
const deliverOrderSenderDisplay: { [key: string]: string } = {
  provable: i18n.tc('deliverOrderSender.options.provable'),
  loanet: i18n.tc('deliverOrderSender.options.loanet'),
  self: i18n.tc('deliverOrderSender.options.self'),
};

const companies = shallowRef<Company[]>([]);
const tableSearch = ref('');
const addBuffer = ref<CompanyRequest | null>(null);
const dialogBuffer = ref<CompanyRequest | null>(null);
const dialogOptions = ref({
  isActive: false,
  selectedCompanyId: null as null | string,
});
const companyIdToManageAllowList = ref<string | null>(null);

const clientConfig = computed(() => vuex.state.clientConfig!);
const visibleCompanies = computed(() =>
  companies.value.filter((company) => company.kind === 'client')
);

const dialogManager = useDialogManager();
const snackbarManager = useSnackbarManager();
const socketStore = useSocketStore();

socketStore.useEventHandler('refreshCompanies', async () => {
  await fetchBrokerCompanies();
});

async function fetchBrokerCompanies(): Promise<void> {
  const response = await axios.get('/api/1/broker-admin/companies');
  const parsed = CompanyListResponse.fromData(response.data);
  companies.value = parsed.companies;
}

async function deleteBrokerCompany(companyId: string): Promise<void> {
  await axios.delete(`/api/1/broker-admin/companies/${companyId}`);
}

async function deleteBrokerCompanyLoans(companyId: string): Promise<void> {
  await axios.delete(`/api/1/broker-admin/companies/${companyId}/loans`);
}

function createCompany(): void {
  dialogOptions.value = {
    isActive: true,
    selectedCompanyId: null,
  };

  // addBuffer is a 2nd buffer behind the dialog so that the admin
  // can escape from `Create New Company` and return with partially filled record
  // browsing the company list will not destroy the partially filled record
  if (addBuffer.value == null) {
    addBuffer.value = CompanyRequest.create();
  }
  dialogBuffer.value = addBuffer.value;
}

function editCompany(rowCompany: Company): void {
  // do not modify the row while typing
  dialogBuffer.value = CompanyRequest.fromCompany(rowCompany);
  dialogOptions.value = {
    isActive: true,
    selectedCompanyId: rowCompany.id,
  };
}

function closeModal(): void {
  // wipe company data
  dialogOptions.value = {
    isActive: false,
    selectedCompanyId: null,
  };
  addBuffer.value = null;
  dialogBuffer.value = null;
}

function deleteCompany(rowCompany: Company): void {
  dialogManager.ask({
    title: i18n.t('deleteBrokerCompany'),
    color: 'error',
    icon: 'mdi-alert',
    message: i18n.t('deleteBrokerCompanyMessage', {
      companyName: rowCompany.name,
    }),
    acceptText: i18n.t('deleteBrokerCompany'),
    onAccept: () => doDeleteCompany(rowCompany),
    rejectText: i18n.t('cancelButton'),
    shouldManuallyConfirm: true,
  });
}

function deleteCompanyLoans(rowCompany: Company): void {
  dialogManager.ask({
    title: i18n.t('deleteBrokerCompanyLoans'),
    color: 'error',
    icon: 'mdi-alert',
    message: i18n.t('deleteBrokerCompanyLoansMessage', {
      companyName: rowCompany.name,
    }),
    acceptText: i18n.t('deleteBrokerCompanyLoans'),
    onAccept: () => doDeleteCompanyLoans(rowCompany),
    rejectText: i18n.t('cancelButton'),
    shouldManuallyConfirm: true,
  });
}

async function doDeleteCompanyLoans(rowCompany: Company): Promise<void> {
  try {
    await deleteBrokerCompanyLoans(rowCompany.id);

    deleteConfirmation('deletedBrokerCompanyLoans');
  } catch (e) {
    handleError(e);
  }
}

async function doDeleteCompany(rowCompany: Company): Promise<void> {
  try {
    await deleteBrokerCompany(rowCompany.id);

    deleteConfirmation('deletedBrokerCompany');
  } catch (e) {
    handleError(e);
  }
}

function deleteConfirmation(msgKey: string): void {
  snackbarManager.confirm(i18n.tc(msgKey));
}

function handleError(err: unknown): void {
  snackbarManager.error(errorString(err as Error));
}

onMounted(async () => {
  try {
    await fetchBrokerCompanies();
  } catch (e) {
    logger.warn(e);
  }
});
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <!-- add company account -->
    <VBtn class="ma-6" color="primary" @click="createCompany">
      <VIcon left> mdi-plus</VIcon>
      {{ $t('addBrokerCompany') }}
    </VBtn>

    <!-- search bar -->
    <VTextField
      v-model="tableSearch"
      class="mx-6"
      clearable
      :label="$t('searchBrokerCompany')"
      prepend-inner-icon="mdi-magnify"
    ></VTextField>

    <VDataTable
      class="elevation-0 pb-8"
      dense
      disable-pagination
      fixed-header
      :headers="tableColumns"
      height="100%"
      hide-default-footer
      :items="visibleCompanies"
      :no-data-text="$tc('no companies', companies.length, { count: companies.length })"
      :search="tableSearch"
      sort-by="name"
    >
      <!-- company icon -->
      <!-- eslint-disable-next-line vue/no-lone-template -->
      <template>
        <VIcon disabled small> mdi-briefcase</VIcon>
      </template>

      <!-- display id -->
      <template #[`item.displayId`]="{ item }">
        {{ item.displayId }}
      </template>

      <!-- trading allowed by this company -->
      <template #[`item.tradingPermissions`]="{ item }">
        {{ $t(`tradingPermissions.options.${item.tradingPermissions}`) }}
      </template>

      <!-- Loanet integration -->
      <template #[`item.loanetIntegration`]="{ item }">
        <VIcon v-if="item.hasLoanetIntegration" color="grey" small>mdi-checkbox-marked</VIcon>
        <VIcon v-else color="grey" small>mdi-checkbox-blank-off-outline</VIcon>
      </template>

      <!-- Loanet integration -->
      <template #[`item.deliverOrderSender`]="{ item }">
        <span>{{ deliverOrderSenderDisplay[item.deliverOrderSender] }}</span>
      </template>

      <template #[`item.preferredIndependentAmountRate`]="{ item }">
        <RateOutput :precision="2" :rate="item.preferredIndependentAmountRate" />
      </template>
      <template #[`item.preferredRoundingRule`]="{ item }">
        {{ roundingRuleToShortString(item.preferredRoundingRule) }}
      </template>

      <!-- action buttons -->
      <template #[`item.actions`]="{ item }">
        <VTooltip color="primary" top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <VBtn
                class="ma-1 mr-10"
                color="primary"
                icon
                :to="{ name: 'broker-admin.accounts.users', query: { filter: item.name } }"
                x-small
              >
                <VIcon> mdi-account-search </VIcon>
              </VBtn>
            </span>
          </template>
          <span>{{ $t('adminSelectUsers') }}</span>
        </VTooltip>

        <VTooltip color="grey" top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <VBtn class="ma-1" color="grey" icon x-small @click="editCompany(item)">
                <VIcon> mdi-briefcase-edit </VIcon>
              </VBtn>
            </span>
          </template>
          <span>{{ $t('editBrokerCompany') }}</span>
        </VTooltip>

        <VTooltip color="error" top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <VBtn class="ma-1" color="error" icon x-small @click="deleteCompany(item)">
                <VIcon> mdi-briefcase-minus </VIcon>
              </VBtn>
            </span>
          </template>
          <span>{{ $t('deleteBrokerCompany') }}</span>
        </VTooltip>

        <VTooltip color="primary" top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <VBtn
                class="ma-1"
                color="primary"
                icon
                x-small
                @click="companyIdToManageAllowList = item.id"
              >
                <VIcon> mdi-ip </VIcon>
              </VBtn>
            </span>
          </template>
          <span>{{ $t('manageAllowList') }}</span>
        </VTooltip>

        <VBtn
          v-if="clientConfig.demoMode"
          class="demo-button"
          color="error"
          outlined
          x-small
          @click="deleteCompanyLoans(item)"
          >Wipe loans and orders
        </VBtn>
      </template>
    </VDataTable>

    <!-- at least 1 company before adding users! -->
    <VAlert
      v-if="!visibleCompanies.length"
      border="left"
      colored-border
      elevation="2"
      text
      type="warning"
    >
      <span v-html="$t('noTraderCompanies')"></span>
    </VAlert>

    <!-- edit company popup -->
    <BrokerCompanyDialog
      v-if="dialogOptions.isActive && dialogBuffer"
      :company-request="dialogBuffer"
      :selected-company-id="dialogOptions.selectedCompanyId"
      @close-modal="closeModal"
    ></BrokerCompanyDialog>

    <BrokerIpAllowListManager
      v-if="companyIdToManageAllowList"
      :company-id.sync="companyIdToManageAllowList"
    />
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .demo-button {
    margin-left: 32px;
  }
}
</style>
